import { PurchaseOrderItemRecord, ThreeWayMatchStatusKey } from '@/types/domain/po-management';

export interface LineItemFinancials {
  lineSubtotal: number;
  taxAmount: number;
  lineTotal: number;
  pendingQuantity: number;
}

export function calculateLineItemTotal(
  quantityOrdered: number,
  unitPrice: number,
  discountAmount: number = 0,
  taxPercent: number = 0
): LineItemFinancials {
  const safeQty = Math.max(0, Number(quantityOrdered) || 0);
  const safePrice = Math.max(0, Number(unitPrice) || 0);
  const safeDiscount = Math.max(0, Number(discountAmount) || 0);
  const safeTax = Math.max(0, Number(taxPercent) || 0);

  const baseAmount = safeQty * safePrice;
  const lineSubtotal = Math.max(0, baseAmount - safeDiscount);
  const taxAmount = Math.round(((lineSubtotal * safeTax) / 100) * 100) / 100;
  const lineTotal = Math.round((lineSubtotal + taxAmount) * 100) / 100;

  return {
    lineSubtotal,
    taxAmount,
    lineTotal,
    pendingQuantity: safeQty,
  };
}

export function calculatePendingQuantity(ordered: number, delivered: number): number {
  const safeOrdered = Math.max(0, Number(ordered) || 0);
  const safeDelivered = Math.max(0, Number(delivered) || 0);
  return Math.max(0, safeOrdered - safeDelivered);
}

export interface OrderFinancialSummary {
  subtotal: number;
  discount: number;
  tax: number;
  freight: number;
  totalValue: number;
  itemCount: number;
  totalOrderedUnits: number;
  totalDeliveredUnits: number;
  totalPendingUnits: number;
}

export function calculateOrderTotals(
  items: (Pick<PurchaseOrderItemRecord, 'quantityOrdered' | 'unitPrice'> &
    Partial<Pick<PurchaseOrderItemRecord, 'quantityDelivered' | 'discountAmount' | 'taxPercent'>>)[],
  overallDiscount: number = 0,
  freight: number = 0
): OrderFinancialSummary {
  let subtotal = 0;
  let tax = 0;
  let totalOrderedUnits = 0;
  let totalDeliveredUnits = 0;
  let totalPendingUnits = 0;

  items.forEach((it) => {
    const calc = calculateLineItemTotal(it.quantityOrdered, it.unitPrice, it.discountAmount, it.taxPercent);
    subtotal += calc.lineSubtotal;
    tax += calc.taxAmount;
    totalOrderedUnits += it.quantityOrdered;
    totalDeliveredUnits += it.quantityDelivered || 0;
    totalPendingUnits += calculatePendingQuantity(it.quantityOrdered, it.quantityDelivered || 0);
  });

  const safeDiscount = Math.max(0, Number(overallDiscount) || 0);
  const safeFreight = Math.max(0, Number(freight) || 0);
  const totalValue = Math.round((subtotal - safeDiscount + tax + safeFreight) * 100) / 100;

  return {
    subtotal: Math.round(subtotal * 100) / 100,
    discount: safeDiscount,
    tax: Math.round(tax * 100) / 100,
    freight: safeFreight,
    totalValue,
    itemCount: items.length,
    totalOrderedUnits,
    totalDeliveredUnits,
    totalPendingUnits,
  };
}

export function deriveThreeWayMatchStatus(
  poAmount: number,
  grnAmount: number | null | undefined,
  invoiceAmount: number | null | undefined,
  orderedQty: number,
  receivedQty: number,
  invoicedQty: number
): ThreeWayMatchStatusKey {
  if (grnAmount === null || grnAmount === undefined || receivedQty === 0) {
    return 'GRN_PENDING';
  }
  if (invoiceAmount === null || invoiceAmount === undefined || invoicedQty === 0) {
    return 'INVOICE_PENDING';
  }
  if (orderedQty !== receivedQty || receivedQty !== invoicedQty) {
    return 'QUANTITY_MISMATCH';
  }
  if (Math.abs(poAmount - invoiceAmount) > 0.01) {
    return 'PRICE_MISMATCH';
  }
  return 'MATCHED';
}

export function detectDeliveryDelay(
  promisedDateStr: string,
  actualDeliveryDateStr?: string,
  isFullyDelivered: boolean = false
): { isDelayed: boolean; delayDays: number } {
  if (!promisedDateStr) return { isDelayed: false, delayDays: 0 };

  const promised = new Date(promisedDateStr).getTime();
  const comparisonTime = isFullyDelivered && actualDeliveryDateStr
    ? new Date(actualDeliveryDateStr).getTime()
    : new Date().getTime();

  if (isNaN(promised) || isNaN(comparisonTime)) {
    return { isDelayed: false, delayDays: 0 };
  }

  const diffMs = comparisonTime - promised;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays > 0) {
    return { isDelayed: true, delayDays: diffDays };
  }

  return { isDelayed: false, delayDays: 0 };
}
